// Lightweight app-event bus for observability. Fires named events with
// structured fields into two sinks in parallel:
//
//   • A local ring buffer (localStorage, RING_SIZE most recent) that
//     powers the in-app /debug timeline so you can see what just
//     happened without leaving the app.
//   • A batched fetch to /api/events which the worker persists in D1 so
//     you can look at events across sessions from the CF dashboard or
//     from /debug's "server tail" toggle.
//
// This is a diagnostics tool, not analytics — nothing is aggregated,
// nothing is sampled, every event with a fields hint that helps you
// answer "what did the app know at the moment it looked wrong?" should
// be logged. Data-source, fallback-used, missing-photo, model
// confidence, api round-trips, trip mutations — all fair game.

interface EventFields {
  [key: string]: string | number | boolean | null | undefined;
}

export interface AppEvent {
  // ISO-8601 timestamp — client wall-clock at the moment of the event.
  ts: string;
  // Kebab / dot-separated name, e.g. "fish.add", "presentation.photo.fallback".
  name: string;
  // Free-form context. Keep values scalar so JSON stringify is cheap
  // and D1 filtering stays simple.
  fields: EventFields;
  // Current client route at emit time — useful when you don't remember
  // which screen you were on when a bug happened.
  route: string;
  // Client-generated session id, so a single flush batch is correlateable
  // across events even before the server assigns row ids.
  session: string;
}

const RING_SIZE = 300;
const FLUSH_INTERVAL_MS = 4000;
const STORAGE_KEY = "reefscope_events_v1";
const SESSION_KEY = "reefscope_events_session_v1";

let ring: AppEvent[] = [];
let pending: AppEvent[] = [];
let flushTimer: number | null = null;

function newSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as { randomUUID(): string }).randomUUID();
  }
  return `s-${Math.floor(Math.random() * 1e9).toString(36)}-${Date.now().toString(36)}`;
}

// A single session id per browser tab lifetime — restarts on hard
// reload, survives SPA navigation. Stored in sessionStorage so opening
// a new tab gets its own id (useful when reproducing something).
function loadSession(): string {
  try {
    if (typeof sessionStorage === "undefined") return newSessionId();
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = newSessionId();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return newSessionId();
  }
}

const session = typeof window === "undefined" ? "server" : loadSession();

function loadRing(): void {
  try {
    if (typeof localStorage === "undefined") return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) ring = parsed.slice(-RING_SIZE);
  } catch {
    /* corrupt payload — start empty */
  }
}
loadRing();

function persistRing(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ring));
  } catch {
    /* quota — silently drop */
  }
}

/**
 * Log a diagnostic event. Fire-and-forget: nothing throws, nothing
 * blocks. Safe to call from render paths (the ring push is O(1) and
 * the network flush is deferred).
 */
export function logEvent(name: string, fields: EventFields = {}): void {
  if (typeof window === "undefined") return;
  // Coerce Date to number, drop functions / objects that would blow up
  // JSON — keep every value scalar so the server can filter cheaply.
  const clean: EventFields = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined) continue;
    if (
      v === null ||
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    ) {
      clean[k] = v;
    }
  }
  const event: AppEvent = {
    ts: new Date().toISOString(),
    name,
    fields: clean,
    route: typeof location !== "undefined" ? location.pathname : "",
    session,
  };
  ring.push(event);
  if (ring.length > RING_SIZE) ring.splice(0, ring.length - RING_SIZE);
  pending.push(event);
  persistRing();
}

/** Reverse-chronological snapshot of the local ring, for the /debug view. */
export function getRecentEvents(): AppEvent[] {
  return [...ring].reverse();
}

/** Wipe the local ring — leaves anything already sent to the server intact. */
export function clearEvents(): void {
  ring = [];
  pending = [];
  persistRing();
}

/**
 * Reset every piece of local diagnostic state — ring, pending queue,
 * session id, localStorage keys. Call this on sign-out so the next
 * signed-in user doesn't flush the previous account's events under their
 * user_id. See events-ring-not-account-scoped.
 */
export function resetEventLocals(): void {
  ring = [];
  pending = [];
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage disabled — ring is already blank */
  }
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

async function flush(): Promise<void> {
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  try {
    const body = JSON.stringify({ events: batch });
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        "/api/events",
        new Blob([body], { type: "application/json" }),
      );
      if (!ok) throw new Error("beacon rejected");
    } else {
      await fetch("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
    }
  } catch {
    // Failed to send — put the batch back at the front so the next flush
    // retries. Cap what we retain so a long offline stretch doesn't grow
    // pending forever.
    pending = [...batch.slice(-RING_SIZE), ...pending].slice(-RING_SIZE);
  }
}

/**
 * Kick off the periodic flush + unload flush. Idempotent — safe to call
 * from main.tsx on every page mount. Also fires an immediate flush of
 * anything the ring loaded from the last session.
 */
export function startEventFlush(): void {
  if (typeof window === "undefined") return;
  if (flushTimer) return;
  // Anything already persisted on the ring from a previous session is
  // already in the ring (for the /debug view). Do NOT re-flush it — the
  // server has its own persistence, and replaying 300 old events on
  // every tab reload burnt 300 D1 INSERTs per user per reload. See
  // events-flush-replays-full-ring.
  flushTimer = window.setInterval(flush, FLUSH_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
  window.addEventListener("pagehide", () => void flush(), { once: false });
}
